import React from "react"
import { useTranslation } from "react-i18next"
import { Info } from "lucide-react"

// Official texts reused by NaDiA and the notice their publishers require next to
// them: Fedlex (federal law, OPubl art. 49 RS 170.512.1) and the Ticino
// "Raccolta delle leggi" (the Bollettino ufficiale prevails over it) and the systematic collections of the cantons on LexWork (subproject 3e). The
// wording itself is UI text like everything else: it lives in it.json
// (officialSourceNotice.<source>.{label,text}), currently the Italian text
// unchanged; subproject 4 will use the publishers' official DE/FR/EN wording.
export type OfficialSource = "fedlex" | "rl" | "lexwork"

const OFFICIAL_SOURCES: OfficialSource[] = ["fedlex", "rl", "lexwork"]

export const isOfficialSource = (value: unknown): value is OfficialSource =>
  value === "fedlex" || value === "rl" || value === "lexwork"

const isHttpUrl = (url?: string | null): url is string => !!url && /^https?:\/\//i.test(url)

type Props = {
  sources: Array<OfficialSource | null | undefined>
  url?: string | null
  className?: string
}

/** One line per official source present, kept visually apart from NaDiA's own text. */
export default function OfficialSourceNotice({ sources, url, className = "" }: Props) {
  const { t } = useTranslation()
  const present = OFFICIAL_SOURCES.filter((s) => sources.includes(s))
  if (present.length === 0) return null
  return (
    <div className={`text-[11px] leading-snug text-gray-500 dark:text-gray-400 space-y-0.5 ${className}`}>
      {present.map((source) => (
        <p key={source} className="flex items-start gap-1">
          <Info size={12} className="mt-[1px] flex-shrink-0" aria-hidden="true" />
          <span>
            <span className="font-medium">{t(`officialSourceNotice.${source}.label`)}:</span> {t(`officialSourceNotice.${source}.text`)}
            {present.length === 1 && isHttpUrl(url) && (
              <>
                {" "}
                <a href={url} target="_blank" rel="noopener noreferrer" className="underline hover:text-primary-600">
                  {t('legalActs.detail.officialLink')}
                </a>
              </>
            )}
          </span>
        </p>
      ))}
    </div>
  )
}
